import React, { useMemo, useState, useEffect } from 'react'
import { KTSVG, toAbsoluteUrl } from '../../../../../../_metronic/helpers'
import { getHost } from '../../../../../services/teamExperienceService'
import { useNearBottom } from '../../../../../hooks/utils/useNearBottom'

const PAGE_SIZE = 10

type Host = {
  id: number
  fullName: string
  email: string
}

type Props = {
  hosts: Host[]
  value: number | null
  onChange: (val: number | null, selectedHostObj?: Host | null) => void
  placeholder?: string
  minChars?: number
  disabled?: boolean
  hostType?: string
}

export function HostsAutocompleteField({
  hosts,
  value,
  onChange,
  placeholder = 'Select a host',
  minChars = 0,
  disabled = false,
  hostType,
}: Props) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [localHosts, setLocalHosts] = useState<Host[]>(hosts)
  const [selectedHostObj, setSelectedHostObj] = useState<Host | null>(null)
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)

  // debounce
  const [debounced, setDebounced] = useState('')

  useEffect(() => {
    const id = setTimeout(() => setDebounced(query.trim()), 2000)
    return () => clearTimeout(id)
  }, [query])

  useEffect(() => {
    setLocalHosts(hosts)
  }, [hosts])

  useEffect(() => {
    setVisibleCount(PAGE_SIZE)
  }, [localHosts])

  useEffect(() => {
    if (value) {
      const found =
        localHosts.find((h) => h.id === value) ||
        hosts.find((h) => h.id === value)

      if (found) {
        setSelectedHostObj(found)
      }
    } else {
      setSelectedHostObj(null)
    }
  }, [value, localHosts, hosts])

  useEffect(() => {
    if (disabled) return

    const fetchHosts = async () => {
      if (debounced.trim() === '') {
        setLocalHosts(hosts)
        return
      }

      // Se conserva el comportamiento original de minChars
      if (debounced.length < minChars) {
        return
      }

      const params: any = {
        offset: 0,
        limit: 100,
        search: debounced,
      }

      if (hostType === 'partner') {
        params.type = 'partner'
      }

      try {
        const response = await getHost(params)

        if (response?.results) {
          setLocalHosts(response.results)
        }
      } catch (error) {
        console.error('Error fetching hosts:', error)
      }
    }

    fetchHosts()
  }, [debounced, hostType, disabled, hosts, minChars])

  const visible = useMemo(() => {
    return localHosts.slice(0, visibleCount)
  }, [localHosts, visibleCount])

  const hasMore = visibleCount < localHosts.length

  const handleScroll = useNearBottom(() => {
    if (hasMore) {
      setVisibleCount((count) =>
        Math.min(count + PAGE_SIZE, localHosts.length)
      )
    }
  })

  const selectedHost = selectedHostObj

  const handleSelect = (hostId: number) => {
    if (disabled) return

    const found =
      localHosts.find((h) => h.id === hostId) ||
      hosts.find((h) => h.id === hostId)

    if (found) {
      setSelectedHostObj(found)
    }

    onChange(hostId, found)
    setQuery('')
    setOpen(false)
  }

  const handleClear = () => {
    if (disabled) return

    setSelectedHostObj(null)
    onChange(null, null)
  }

  return (
    <div className="nb-language-autocomplete">
      <div className="nb-language-wrapper">
        {selectedHost ? (
          <div className="tap-add-experience-2-category-pill tap-add-experience-2-category-pill--selected">
            <span>{selectedHost.fullName}</span>

            <div
              className={disabled ? 'disabled' : ''}
              onClick={!disabled ? handleClear : undefined}
            >
              <KTSVG path={toAbsoluteUrl('/media/svg/nobilis/remove.svg')} />
            </div>
          </div>
        ) : (
          <input
            disabled={disabled}
            className="tap-add-experience-input-category nb-language-input"
            placeholder={placeholder}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value)
              setOpen(true)
            }}
            onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 120)}
          />
        )}
      </div>

      {!disabled && open && !selectedHost && visible.length > 0 && (
        <div className="nb-language-menu" onScroll={handleScroll}>
          {visible.map((host) => (
            <div
              key={host.id}
              className="nb-language-item"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleSelect(host.id)}
            >
              {host.fullName} ({host.email})
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
